import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router';
import activityService, { Activity, FeedFilter } from '../services/activityService';
import './ActivityFeed.css';

const ActivityFeed: React.FC = () => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FeedFilter>('all');
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [loadingMore, setLoadingMore] = useState(false);

  const loadFeed = useCallback(async (token?: string, currentFilter?: FeedFilter) => {
    try {
      if (token) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await activityService.getCombinedFeed(20, currentFilter || filter, token);

      if (token) {
        setActivities(prev => [...prev, ...response.activities]);
      } else {
        setActivities(response.activities);
      }
      setNextToken(response.nextToken);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load activity feed';
      setError(message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filter]);

  useEffect(() => {
    loadFeed(undefined, filter);
  }, [filter, loadFeed]);

  const handleFilterChange = (newFilter: FeedFilter) => {
    if (newFilter !== filter) {
      setFilter(newFilter);
      setActivities([]);
      setNextToken(undefined);
    }
  };

  const handleLoadMore = () => {
    if (nextToken && !loadingMore) {
      loadFeed(nextToken);
    }
  };

  const getActivityIcon = (type: Activity['type']): string => {
    switch (type) {
      case 'stream_upload':
        return '🎵';
      case 'stream_play':
        return '▶️';
      case 'follow':
        return '👤';
      case 'favorite':
        return '❤️';
      case 'comment':
        return '💬';
      case 'rating':
        return '⭐';
      default:
        return '📌';
    }
  };

  const getActivityMessage = (activity: Activity): string => {
    const name = activity.actorDisplayName || activity.actorUsername;
    switch (activity.type) {
      case 'stream_upload':
        return `${name} uploaded "${activity.streamName}"`;
      case 'stream_play':
        return `${name} listened to "${activity.streamName}"`;
      case 'follow':
        return `${name} followed @${activity.targetUsername}`;
      case 'favorite':
        return `${name} favorited "${activity.streamName}"`;
      case 'comment':
        return `${name} commented on "${activity.streamName}"`;
      case 'rating':
        return `${name} rated "${activity.streamName}"`;
      default:
        return `${name} did something`;
    }
  };

  const getActivityLink = (activity: Activity): string => {
    switch (activity.type) {
      case 'stream_upload':
      case 'stream_play':
      case 'favorite':
      case 'comment':
      case 'rating':
        return activity.streamId ? `/streams/${activity.streamId}` : '#';
      case 'follow':
        return activity.targetUsername ? `/users/${activity.targetUsername}` : '#';
      default:
        return '#';
    }
  };

  if (loading) {
    return (
      <div className="activity-page">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading activity feed...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="activity-page">
        <div className="error-container">
          <p>{error}</p>
          <button onClick={() => loadFeed()} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="activity-page">
      <header className="page-header">
        <h1>Activity Feed</h1>
        <p className="subtitle">See what you and people you follow are up to</p>
      </header>

      <div className="filter-tabs">
        <button
          className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => handleFilterChange('all')}
        >
          All
        </button>
        <button
          className={`filter-tab ${filter === 'streams' ? 'active' : ''}`}
          onClick={() => handleFilterChange('streams')}
        >
          Streams
        </button>
        <button
          className={`filter-tab ${filter === 'social' ? 'active' : ''}`}
          onClick={() => handleFilterChange('social')}
        >
          Social
        </button>
      </div>

      {activities.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📰</div>
          <h3>No activity yet</h3>
          <p>Follow some creators to see their activity here.</p>
          <Link to="/streams" className="btn btn-primary">
            Discover Creators
          </Link>
        </div>
      ) : (
        <>
          <div className="activity-list">
            {activities.map((activity, index) => (
              <Link
                key={`${activity.userId}-${activity.timestamp}-${index}`}
                to={getActivityLink(activity)}
                className="activity-item"
              >
                <div className="activity-avatar">
                  {activity.actorProfilePicture ? (
                    <img
                      src={activity.actorProfilePicture}
                      alt={activity.actorUsername}
                    />
                  ) : (
                    <div className="avatar-placeholder">
                      {activity.actorUsername.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="activity-type-icon">
                    {getActivityIcon(activity.type)}
                  </span>
                </div>

                <div className="activity-content">
                  <p className="activity-message">
                    {getActivityMessage(activity)}
                  </p>
                  <span className="activity-time">{activity.timeAgo}</span>
                </div>

                {activity.streamCoverImage && (
                  <div className="activity-thumbnail">
                    <img src={activity.streamCoverImage} alt="" />
                  </div>
                )}
              </Link>
            ))}
          </div>

          {nextToken && (
            <div className="load-more-container">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="btn btn-secondary"
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ActivityFeed;
